import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import axios from '../../axiosConfig';
import { mergeCart } from '../../features/cart/cartSlice';
import './CheckoutLinkLanding.css';

/**
 * Sales Tools — Phase 3.2.
 *
 * Customer lands here from a checkout link the merchant sent
 *   (e.g. https://{storeSlug}.{platform}/checkout/link/abc123...).
 *
 * Flow:
 *   1. POST /api/cart/:slug/seed-from-link/:token → server replaces the
 *      session cart with the link's frozen cart and returns customer
 *      pre-fill data.
 *   2. We stash the pre-fill + the link token in sessionStorage under
 *      `checkoutLinkPrefill`. The CheckoutModal reads + clears this key
 *      on mount.
 *   3. dispatch(mergeCart()) so Redux sees the new cart items.
 *   4. Call onOpenCart() from outletContext (provided by StorefrontLayout)
 *      to open the CheckoutModal — direct call avoids the
 *      mount-time-only useEffect race in the layout.
 *   5. navigate('/', { replace: true }) — modal stays open over the home
 *      page; the customer never sees the landing screen for long.
 *
 * Expired / cancelled / used links land us on the inline ExpiredCard
 * with a single CTA back to the homepage. No modal layering.
 */
export default function CheckoutLinkLanding() {
  const { token } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { store, onOpenCart } = useOutletContext() || {};

  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function hydrate() {
      if (!store?.slug || !token) return;
      try {
        // Use the configured axios instance — its request interceptor
        // attaches the X-Cart-ID header from localStorage and its
        // response interceptor captures any new X-Cart-ID. Without this
        // round-trip the backend writes the cart under a *new* bucket
        // while the rest of the storefront keeps reading the old one,
        // and the customer sees an empty cart.
        const resp = await axios.post(
          `/cart/${store.slug}/seed-from-link/${encodeURIComponent(token)}`
        );
        const data = resp?.data || {};

        // Stash for the CheckoutModal to pick up + clear on its own.
        try {
          sessionStorage.setItem(
            'checkoutLinkPrefill',
            JSON.stringify({
              token,
              customerInfo: data.customerInfo || null,
              discountCode: data.discountCode || null,
              expiresAt: data.expiresAt || null,
              setAt: Date.now(),
            })
          );
        } catch {
          // sessionStorage unavailable (e.g. Safari private mode) — proceed
          // without prefill; the modal still opens with the hydrated cart.
        }

        // Refresh Redux cart from server so the modal sees the items.
        await dispatch(mergeCart());

        if (!cancelled) {
          // Open the CheckoutModal first — StorefrontLayout owns its
          // open state and this callback flips it directly. Then route
          // back to the homepage so the modal sits over normal content.
          if (typeof onOpenCart === 'function') {
            onOpenCart();
          }
          navigate('/', { replace: true });
        }
      } catch (err) {
        if (!cancelled) {
          const status = err?.response?.status;
          const msg = err?.response?.data?.error;
          setError(
            status === 410 || status === 403
              ? msg || 'This checkout link is no longer valid.'
              : 'We could not load this checkout link. Please try again.'
          );
        }
      }
    }

    hydrate();
    return () => {
      cancelled = true;
    };
  }, [store?.slug, token, dispatch, navigate, onOpenCart]);

  if (error) {
    return (
      <div className="sl-landing">
        <div className="sl-landing-card">
          <h1 className="sl-landing-title">Link is no longer valid</h1>
          <p className="sl-landing-sub">{error}</p>
          <button
            type="button"
            className="sl-landing-cta"
            onClick={() => navigate('/', { replace: true })}
          >
            Continue shopping
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="sl-landing">
      <div className="sl-landing-card">
        <div className="sl-landing-spinner" aria-hidden />
        <h1 className="sl-landing-title">Loading your cart</h1>
        <p className="sl-landing-sub">
          We&apos;re pulling up the items {store?.name || 'the store'} sent you.
        </p>
      </div>
    </div>
  );
}
